'use client'

import { cn } from '@/lib/utils'
import { MapPin } from 'lucide-react'

interface MapEmbedBlockProps {
  content: Record<string, unknown>
  className?: string
  address?: string | null
  city?: string | null
  state?: string | null
  postalCode?: string | null
  country?: string | null
}

export function MapEmbedBlock({ className, address, city, state, postalCode }: MapEmbedBlockProps) {
  // Build a formatted address for display and Google Maps link
  const parts = [address, city, state, postalCode].filter(Boolean)
  const fullAddress = parts.join(', ')

  if (!fullAddress) {
    return (
      <div className={cn('p-4 rounded-lg bg-zinc-800/30 border border-zinc-700/50 text-center', className)}>
        <MapPin className="h-8 w-8 text-zinc-600 mx-auto mb-2" />
        <p className="text-sm text-zinc-500">No address set</p>
        <p className="text-xs text-zinc-600 mt-1">Update the venue profile to show a map.</p>
      </div>
    )
  }

  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullAddress)}`

  return (
    <div className={cn('space-y-3', className)}>
      <h3 className="text-lg font-semibold text-white">Location</h3>
      <a
        href={mapsUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={cn(
          'flex items-center gap-4 p-4 rounded-xl',
          'bg-gradient-to-r from-zinc-800/80 to-zinc-800/40',
          'border border-zinc-700/50 hover:border-violet-500/30',
          'transition-all hover:shadow-lg hover:shadow-violet-500/5',
          'group'
        )}
      >
        <div className="p-3 rounded-lg bg-violet-500/20 text-violet-400 group-hover:bg-violet-500/30 transition-colors">
          <MapPin className="h-6 w-6" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-white group-hover:text-violet-300 transition-colors text-sm">
            {fullAddress}
          </p>
          <p className="text-xs text-zinc-500 mt-0.5">Open in Google Maps</p>
        </div>
      </a>
    </div>
  )
}
